import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDroppable, useDraggable, closestCorners,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus, CheckSquare, AlertTriangle, CheckCircle2, Circle,
  Loader2, Search, X, CalendarDays, List, LayoutGrid,
  Trash2, Flag, Link2, User, RefreshCw, Tag, Pencil,
  ChevronRight, CalendarClock, MessageSquare,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useWS } from "../context/WebSocketContext";
import Sidebar from "../components/shared/Sidebar";
import Navbar from "../components/shared/Navbar";

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  pending:     { label: "Pendente",     color: "text-slate-400",  bg: "bg-slate-400/10",  dot: "bg-slate-400",  icon: Circle        },
  completed:   { label: "Concluída",    color: "text-green-400",  bg: "bg-green-400/10",  dot: "bg-green-400",  icon: CheckCircle2  },
  overdue:     { label: "Atrasada",     color: "text-red-400",    bg: "bg-red-400/10",    dot: "bg-red-400",    icon: AlertTriangle },
  cancelled:   { label: "Cancelada",    color: "text-slate-500",  bg: "bg-slate-500/10",  dot: "bg-slate-500",  icon: X             },
};

const PRIORITY_CONFIG = {
  low:    { label: "Baixa",   color: "text-slate-400",  bg: "bg-slate-400/10",  border: "border-slate-400/30" },
  medium: { label: "Média",   color: "text-blue-400",   bg: "bg-blue-400/10",   border: "border-blue-400/30"  },
  high:   { label: "Alta",    color: "text-orange-400", bg: "bg-orange-400/10", border: "border-orange-400/30"},
  urgent: { label: "Urgente", color: "text-red-400",    bg: "bg-red-400/10",    border: "border-red-400/30"   },
};

const KANBAN_COLS = [
  { id: "today",       label: "Hoje",         accent: "border-green-500/40",header: "text-green-400", count: "bg-green-500/20 text-green-400" },
  { id: "tomorrow",    label: "Tarefas de Amanhã", accent: "border-sky-500/40", header: "text-sky-400", count: "bg-sky-500/20 text-sky-400" },
  { id: "overdue",     label: "Atrasadas",    accent: "border-red-500/40",  header: "text-red-400",   count: "bg-red-500/20 text-red-400",  noDropTarget: true },
  { id: "week",        label: "Tarefas da semana", accent: "border-emerald-500/40", header: "text-emerald-400", count: "bg-emerald-500/20 text-emerald-400" },
];

const COMPLETED_KANBAN_COL = {
  id: "completed",
  label: "Concluídas",
  accent: "border-green-500/40",
  header: "text-green-400",
  count: "bg-green-500/20 text-green-400",
  noDropTarget: true,
};

// Maps kanban column id → task status to set on drop
const COL_STATUS_MAP = {
  today:       "pending",
  tomorrow:    "pending",
  week:        "pending",
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }); }
  catch { return null; }
}

function fmtDateTime(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return null; }
}

/**
 * Retorna a data LOCAL do usuário no formato YYYY-MM-DD.
 * Usa getFullYear/getMonth/getDate (fuso local) em vez de toISOString()
 * que retorna UTC — em Brasil (UTC-3) isso causaria exibir o dia errado
 * após as 21h (próximo dia em UTC, dia atual no Brasil).
 */
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function tomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Converte qualquer ISO string de data para YYYY-MM-DD no fuso LOCAL.
 *
 * IMPORTANTE: strings no formato puro "YYYY-MM-DD" (sem hora) são interpretadas
 * pelo JS como UTC meia-noite — em UTC-3 isso resulta em "dia anterior 21:00",
 * causando o bug de "-1 dia" no calendário. Por isso, detectamos esse padrão e
 * retornamos a string diretamente, sem passar por new Date().
 */
function toLocalDate(iso) {
  if (!iso) return null;
  const str = String(iso);
  // Strings de data pura (YYYY-MM-DD) — retornar diretamente, sem parsear como UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  try {
    const d = new Date(str);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch { return null; }
}

function isClosedTask(task) {
  return ["completed", "cancelled", "archived"].includes(task?.status) || task?.archived;
}

function shouldShowTask(task, statusFilter) {
  if (statusFilter === "completed") return task?.status === "completed";
  if (statusFilter === "cancelled") return task?.status === "cancelled";
  if (statusFilter === "pending") return ["pending", "in_progress", "overdue"].includes(task?.status) && !isClosedTask(task);
  if (statusFilter === "overdue") return isOverdue(task);
  return !isClosedTask(task);
}

function parseDueDate(value) {
  if (!value) return null;
  const raw = String(value);
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T23:59:59` : raw;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isThisWeek(task) {
  if (isClosedTask(task)) return false;
  const due = parseDueDate(task?.due_date);
  if (!due) return false;
  const now = new Date();
  const start = new Date(now);
  const day = start.getDay() || 7;
  start.setDate(start.getDate() - day + 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return due >= start && due < end;
}

function getTaskCol(task, statusFilter = "") {
  if (!task) return "pending";
  if (task?.status === "completed" && statusFilter === "completed") return "completed";
  if (isClosedTask(task)) return null;
  const today = todayStr();
  const tomorrow = tomorrowStr();
  const due = toLocalDate(task.due_date);
  if (isOverdue(task)) return "overdue";
  if (due === today) return "today";
  if (due === tomorrow) return "tomorrow";
  if (isThisWeek(task)) return "week";
  return null;
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function isOverdue(task) {
  if (!task?.due_date || isClosedTask(task)) return false;
  const due = parseDueDate(task.due_date);
  return due !== null && due < new Date();
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color} ${cfg.bg}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
      <Flag className="w-2.5 h-2.5" /> {cfg.label}
    </span>
  );
}

function AssigneeAvatar({ name, size = "sm" }) {
  const sz = size === "sm" ? "w-6 h-6 text-[10px]" : "w-8 h-8 text-xs";
  if (!name) return (
    <div className={`${sz} rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center`}>
      <User className="w-3 h-3 text-slate-500" />
    </div>
  );
  return (
    <div className={`${sz} rounded-full bg-gradient-to-br from-[#25D366] to-[#1EAC52] flex items-center justify-center font-bold text-slate-950 shrink-0`} title={name}>
      {getInitials(name)}
    </div>
  );
}

function StatCard({ label, value, color = "text-slate-100", onClick, active }) {
  return (
    <button
      onClick={onClick}
      className={`bg-slate-900 border rounded-xl p-4 text-left transition-all hover:border-slate-600 ${active ? "border-[#25D366] ring-1 ring-[#25D366]/30" : "border-slate-800"}`}
    >
      <div className={`text-2xl font-bold ${color}`}>{value ?? 0}</div>
      <div className="text-slate-400 text-xs mt-0.5">{label}</div>
    </button>
  );
}

// ── Draggable Task Card (Kanban) ───────────────────────────────────────────────

function DraggableTaskCard({ task, onEdit, onComplete, onDelete, onOpenChat, index }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task.id,
    data: { task },
  });

  const overdue = task.status === "overdue" || isOverdue(task);
  const done = task.status === "completed";

  return (
    <motion.div
      ref={setNodeRef}
      // Aplicar transform do DnD apenas quando arrastando; não sobrescrever
      // animação do framer-motion quando o card está parado
      style={isDragging ? { transform: CSS.Translate.toString(transform) } : undefined}
      {...attributes}
      {...listeners}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: isDragging ? 0.4 : 1, y: 0 }}
      transition={{ duration: 0.18, delay: Math.min(index * 0.04, 0.25) }}
      className={`group bg-slate-800 border rounded-lg p-3 cursor-grab active:cursor-grabbing select-none
        hover:border-slate-500 hover:-translate-y-0.5 transition-all shadow-sm
        ${isDragging ? "shadow-2xl rotate-1 z-50" : ""}
        ${overdue && !done ? "border-red-500/30" : "border-slate-700"}
        ${done ? "opacity-60" : ""}
      `}
      // Click abre o drawer — só se não estava arrastando
      onClick={() => { if (!isDragging) onEdit(task); }}
    >
      {/* Priority + overdue indicator */}
      <div className="flex items-center gap-1.5 mb-2">
        <PriorityBadge priority={task.priority} />
        {overdue && !done && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/10 border border-red-500/30 text-red-400">
            <AlertTriangle className="w-2.5 h-2.5" /> Atrasada
          </span>
        )}
      </div>

      {/* Title */}
      <p className={`text-sm font-medium leading-snug mb-2 ${done ? "line-through text-slate-500" : "text-slate-100"}`}>
        {task.title}
      </p>

      {/* Lead link */}
      {task.lead_name && (
        <div className="flex items-center justify-between gap-1 mb-2">
          <div className="flex items-center gap-1 min-w-0">
            <Link2 className="w-3 h-3 text-slate-500 shrink-0" />
            <span className="text-[11px] text-slate-400 truncate">{task.lead_name}</span>
          </div>
          {task.lead_id && onOpenChat && (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenChat(task); }}
              className="shrink-0 p-1 text-slate-500 hover:text-[#25D366] hover:bg-slate-700/60 rounded transition"
              title={task.suggested_message ? "Abrir chat com mensagem sugerida" : "Abrir chat do lead"}
            >
              <MessageSquare className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* Footer: assignee + due date */}
      <div className="flex items-center justify-between gap-2 mt-2">
        <div className="flex items-center gap-1.5">
          <AssigneeAvatar name={task.assigned_to_name} size="sm" />
          {task.assigned_to_name && (
            <span className="text-[10px] text-slate-500 truncate max-w-[80px]">{task.assigned_to_name}</span>
          )}
        </div>
        {task.due_date && (
          <span className={`text-[10px] font-medium flex items-center gap-1 ${overdue && !done ? "text-red-400" : "text-slate-500"}`}>
            <CalendarClock className="w-3 h-3" />
            {fmtDate(task.due_date)}
          </span>
        )}
      </div>

      {/* Quick complete button — stopPropagation prevents opening drawer */}
      <button
        onClick={(e) => { e.stopPropagation(); onComplete(task); }}
        className={`mt-2.5 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md border text-[11px] font-medium transition-colors ${
          done
            ? "border-green-500/20 text-green-400 hover:border-slate-600 hover:text-slate-400"
            : "border-slate-700 text-slate-400 hover:text-[#25D366] hover:border-[#25D366]/40"
        }`}
      >
        {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
        {done ? "Concluída" : "Concluir"}
      </button>
    </motion.div>
  );
}

// ── Droppable Kanban Column ───────────────────────────────────────────────────

function KanbanTaskColumn({ col, tasks, onEdit, onComplete, onDelete, onOpenChat }) {
  const { isOver, setNodeRef } = useDroppable({ id: col.id });

  return (
    <div className="flex-shrink-0 w-72 flex flex-col">
      {/* Header */}
      <div className={`flex items-center justify-between mb-3 pb-2 border-b ${col.accent}`}>
        <span className={`text-sm font-semibold ${col.header}`}>{col.label}</span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${col.count}`}>{tasks.length}</span>
      </div>

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        className={`flex-1 flex flex-col gap-2 min-h-24 rounded-xl p-1.5 transition-colors ${
          isOver && !col.noDropTarget ? "bg-slate-700/20 ring-1 ring-slate-600/40" : ""
        } ${col.noDropTarget && isOver ? "ring-1 ring-red-500/30 bg-red-500/5" : ""}`}
      >
        {tasks.map((task, i) => (
          <DraggableTaskCard
            key={task.id}
            task={task}
            index={i}
            onEdit={onEdit}
            onComplete={onComplete}
            onDelete={onDelete}
            onOpenChat={onOpenChat}
          />
        ))}
        {tasks.length === 0 && !col.noDropTarget && (
          <div className="flex-1 flex items-center justify-center py-8 text-slate-700 text-[11px]">—</div>
        )}
      </div>
    </div>
  );
}

// ── Task List Row ──────────────────────────────────────────────────────────────

function TaskListRow({ task, onEdit, onComplete, onDelete, onOpenChat }) {
  const overdue = isOverdue(task) || task.status === "overdue";
  const done = task.status === "completed";
  return (
    <div className={`group flex items-start gap-3 px-5 py-3.5 hover:bg-slate-800/30 transition-colors border-b border-slate-800/50 last:border-0 ${done ? "opacity-60" : ""}`}>
      <button
        onClick={() => onComplete(task)}
        className={`mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
          done ? "border-green-400 bg-green-400/20 text-green-400" : "border-slate-600 hover:border-[#25D366] text-transparent hover:text-[#25D366]"
        }`}
        title={done ? "Reabrir" : "Concluir"}
      >
        <CheckCircle2 className="w-3 h-3" />
      </button>

      <div className="flex-1 min-w-0" onClick={() => onEdit(task)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onEdit(task)}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium ${done ? "line-through text-slate-500" : overdue ? "text-red-300" : "text-slate-100"}`}>
            {task.title}
          </span>
          <StatusBadge status={task.status} />
          <PriorityBadge priority={task.priority} />
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {task.lead_name && (
            <span className="flex items-center gap-1 text-[11px] text-slate-500">
              <Link2 className="w-3 h-3" /> {task.lead_name}
            </span>
          )}
          {task.assigned_to_name && (
            <span className="flex items-center gap-1 text-[11px] text-slate-500">
              <User className="w-3 h-3" /> {task.assigned_to_name}
            </span>
          )}
          {task.due_date && (
            <span className={`flex items-center gap-1 text-[11px] ${overdue && !done ? "text-red-400" : "text-slate-500"}`}>
              <CalendarClock className="w-3 h-3" /> {fmtDate(task.due_date)}
            </span>
          )}
          {(task.tags || []).length > 0 && (
            <span className="flex items-center gap-1 text-[11px] text-slate-500">
              <Tag className="w-3 h-3" /> {task.tags.slice(0, 2).join(", ")}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        {task.lead_id && onOpenChat && (
          <button
            onClick={() => onOpenChat(task)}
            className="p-1.5 text-slate-500 hover:text-[#25D366] hover:bg-slate-800 rounded transition"
            title={task.suggested_message ? "Abrir chat com mensagem sugerida" : "Abrir chat do lead"}
          >
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
        )}
        <button onClick={() => onEdit(task)} className="p-1.5 text-slate-500 hover:text-[#25D366] hover:bg-slate-800 rounded transition" title="Editar">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(task)} className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded transition" title="Excluir">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Calendar View ──────────────────────────────────────────────────────────────

function CalendarView({ tasks, onEdit, year, month, onMonthChange }) {
  const today = todayStr();
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = new Date(year, month - 1, 1).getDay();

  const tasksByDay = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      // Usar fuso local para agrupar por dia, evitando erro UTC (ex: UTC-3)
      const d = toLocalDate(t.due_date);
      if (!d) return;
      (map[d] = map[d] || []).push(t);
    });
    return map;
  }, [tasks]);

  const monthLabel = new Date(year, month - 1, 1).toLocaleString("pt-BR", { month: "long", year: "numeric" });
  const blanks = Array(firstDay).fill(null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <button onClick={() => onMonthChange(-1)} className="p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-lg transition">
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
        <span className="text-slate-100 font-semibold capitalize text-sm min-w-[160px] text-center">{monthLabel}</span>
        <button onClick={() => onMonthChange(1)} className="p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-lg transition">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
          <div key={d} className="text-center text-[10px] font-semibold text-slate-500 py-1">{d}</div>
        ))}
        {blanks.map((_, i) => <div key={`b${i}`} />)}
        {days.map((day) => {
          const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const dayTasks = tasksByDay[dateStr] || [];
          const isToday = dateStr === today;
          const isPast = dateStr < today;
          return (
            <div
              key={day}
              className={`rounded-lg p-1.5 min-h-[60px] transition-colors ${
                isToday ? "bg-green-500/10 ring-1 ring-green-500/40" : isPast && dayTasks.length > 0 ? "bg-red-500/5" : "hover:bg-slate-800/40"
              }`}
            >
              <div className={`text-[11px] font-semibold mb-1 ${isToday ? "text-green-400" : isPast && dayTasks.length > 0 ? "text-red-400" : "text-slate-500"}`}>
                {day}
              </div>
              <div className="space-y-0.5">
                {dayTasks.slice(0, 3).map((t) => {
                  const cfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.pending;
                  return (
                    <button
                      key={t.id}
                      onClick={() => onEdit(t)}
                      className="w-full text-left truncate text-[10px] leading-tight px-1 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border-l-2"
                      style={{ borderColor: cfg.dot.replace("bg-", "") }}
                    >
                      {t.title}
                    </button>
                  );
                })}
                {dayTasks.length > 3 && (
                  <div className="text-[9px] text-slate-500 pl-1">+{dayTasks.length - 3} mais</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Task Drawer (create / edit) ────────────────────────────────────────────────

const EMPTY_FORM = {
  title: "", description: "", status: "pending", priority: "medium",
  assigned_to: "", due_date: "", lead_id: "", lead_name: "", tags: "",
};

function TaskDrawer({ open, task, onClose, onSaved, onDeleted, team, currentUser }) {
  const isEdit = !!task?.id;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        status: task.status === "in_progress" ? "pending" : (task.status || "pending"),
        priority: task.priority || "medium",
        assigned_to: task.assigned_to || "",
        due_date: task.due_date ? String(task.due_date).slice(0, 16) : "",
        lead_id: task.lead_id || "",
        lead_name: task.lead_name || "",
        tags: (task.tags || []).join(", "),
      });
    } else {
      setForm(EMPTY_FORM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id]);

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Título é obrigatório"); return; }
    setSaving(true);
    try {
      // Para datas sem hora (YYYY-MM-DD), anexar T12:00:00 antes de converter para ISO.
      // Isso garante que a conversão UTC nunca recue para o dia anterior em UTC-3.
      const dueDateIso = form.due_date
        ? new Date(
            /^\d{4}-\d{2}-\d{2}$/.test(form.due_date)
              ? form.due_date + "T12:00:00"
              : form.due_date
          ).toISOString()
        : null;
      const payload = {
        title: form.title.trim(),
        description: form.description || null,
        status: form.status,
        priority: form.priority,
        assigned_to: form.assigned_to || null,
        due_date: dueDateIso,
        lead_id: form.lead_id || null,
        lead_name: form.lead_name || null,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      };
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/tasks/${task.id}`, payload);
        saved = data;
        toast.success("Tarefa atualizada");
      } else {
        const { data } = await api.post("/tasks", payload);
        saved = data;
        toast.success("Tarefa criada");
      }
      onSaved(saved, isEdit);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar tarefa");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!task?.id) return;
    if (!window.confirm("Excluir esta tarefa?")) return;
    setDeleting(true);
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Tarefa excluída");
      onDeleted(task.id);
      onClose();
    } catch {
      toast.error("Erro ao excluir tarefa");
    } finally {
      setDeleting(false);
    }
  };

  const inputCls = "w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:border-[#25D366] transition";
  const labelCls = "block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 z-40"
          />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-y-0 right-0 w-full sm:w-[440px] bg-slate-900 border-l border-slate-700 z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
              <h2 className="text-base font-bold text-slate-50" style={{ fontFamily: "Outfit" }}>
                {isEdit ? "Editar Tarefa" : "Nova Tarefa"}
              </h2>
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-md transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
              {/* Title */}
              <div>
                <label className={labelCls}>Título *</label>
                <input
                  autoFocus
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                  placeholder="Descreva a tarefa..."
                  className={inputCls}
                />
              </div>

              {/* Description */}
              <div>
                <label className={labelCls}>Descrição</label>
                <textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Detalhes opcionais..."
                  rows={3}
                  className={`${inputCls} resize-none`}
                />
              </div>

              {/* Status + Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Status</label>
                  <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Prioridade</label>
                  <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className={inputCls}>
                    {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Assignee + Due date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Responsável</label>
                  <select value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)} className={inputCls}>
                    <option value="">— Nenhum —</option>
                    {team.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Vencimento</label>
                  <input
                    type="datetime-local"
                    value={form.due_date}
                    onChange={(e) => set("due_date", e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Lead name */}
              <div>
                <label className={labelCls}>Lead vinculado</label>
                <input
                  value={form.lead_name}
                  onChange={(e) => set("lead_name", e.target.value)}
                  placeholder="Nome do lead (opcional)"
                  className={inputCls}
                />
              </div>

              {/* Tags */}
              <div>
                <label className={labelCls}>Tags (separadas por vírgula)</label>
                <input
                  value={form.tags}
                  onChange={(e) => set("tags", e.target.value)}
                  placeholder="ex: reunião, proposta, urgente"
                  className={inputCls}
                />
              </div>

              {/* Edit metadata */}
              {isEdit && (
                <div className="text-[11px] text-slate-600 space-y-0.5 pt-2 border-t border-slate-800">
                  {task.created_by_name && <p>Criado por <span className="text-slate-500">{task.created_by_name}</span></p>}
                  {task.created_at && <p>Em <span className="text-slate-500">{fmtDateTime(task.created_at)}</span></p>}
                  {task.completed_at && <p>Concluída em <span className="text-slate-500">{fmtDateTime(task.completed_at)}</span></p>}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-slate-800 flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 rounded-lg bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 text-sm font-bold disabled:opacity-60 transition flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {saving ? "Salvando..." : isEdit ? "Salvar alterações" : "Criar tarefa"}
              </button>
              {isEdit && (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="p-2.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-60 transition"
                  title="Excluir tarefa"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ── Ghost card for DragOverlay ─────────────────────────────────────────────────

function GhostCard({ task }) {
  return (
    <div className="bg-slate-800 border border-[#25D366]/40 rounded-lg p-3 shadow-2xl w-64 opacity-95 rotate-2">
      <div className="flex items-center gap-1.5 mb-2">
        <PriorityBadge priority={task.priority} />
      </div>
      <p className="text-sm font-medium text-slate-100 truncate">{task.title}</p>
      {task.lead_name && (
        <div className="flex items-center gap-1 mt-1.5">
          <Link2 className="w-3 h-3 text-slate-500" />
          <span className="text-[11px] text-slate-400 truncate">{task.lead_name}</span>
        </div>
      )}
    </div>
  );
}

// ── Main Tasks page ────────────────────────────────────────────────────────────

export default function Tasks() {
  const { user } = useAuth();
  const { on } = useWS();
  const navigate = useNavigate();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban"); // "kanban" | "list" | "calendar"
  const [activeTask, setActiveTask] = useState(null); // being dragged

  // Drag-to-scroll state for the kanban container
  const kanbanScrollRef = useRef(null);
  const scrollDragging = useRef(false);
  const scrollStartX = useRef(0);
  const scrollLeft = useRef(0);

  const handleKanbanMouseDown = (e) => {
    // Only left-click, and only on the background (not on cards)
    if (e.button !== 0) return;
    if (e.target.closest(".cursor-grab")) return;
    scrollDragging.current = true;
    scrollStartX.current = e.pageX - (kanbanScrollRef.current?.offsetLeft || 0);
    scrollLeft.current = kanbanScrollRef.current?.scrollLeft || 0;
    if (kanbanScrollRef.current) kanbanScrollRef.current.style.cursor = "grabbing";
  };
  const handleKanbanMouseMove = (e) => {
    if (!scrollDragging.current || !kanbanScrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - kanbanScrollRef.current.offsetLeft;
    kanbanScrollRef.current.scrollLeft = scrollLeft.current - (x - scrollStartX.current) * 1.2;
  };
  const handleKanbanMouseUp = () => {
    scrollDragging.current = false;
    if (kanbanScrollRef.current) kanbanScrollRef.current.style.cursor = "";
  };

  // Filters
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterMine, setFilterMine] = useState(false);
  const [filterOverdue, setFilterOverdue] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTask, setDrawerTask] = useState(null); // null = create

  // Calendar navigation
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus && !["pending", "overdue"].includes(filterStatus)) params.set("status", filterStatus);
      if (filterPriority) params.set("priority", filterPriority);
      if (filterMine) params.set("mine", "true");
      if (filterOverdue) params.set("overdue", "true");
      if (search) params.set("search", search);
      params.set("limit", "300");
      const { data } = await api.get(`/tasks?${params}`);
      const items = Array.isArray(data) ? data : [];
      setTasks(items.filter((task) => shouldShowTask(task, filterStatus)));
    } catch {
      toast.error("Erro ao carregar tarefas");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, filterMine, filterOverdue, search]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks/dashboard");
      setStats(data || {});
    } catch {}
  }, []);

  const fetchTeam = useCallback(async () => {
    try {
      const { data } = await api.get("/team");
      setTeam(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => { fetchStats(); fetchTeam(); }, [fetchStats, fetchTeam]);

  // ── Realtime ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const isAdminRole = ["owner", "admin", "super_admin"].includes(user?.role);

    const u1 = on("task.created", (p) => {
      if (!p?.task) return;
      // Agents only see tasks assigned to themselves
      if (!isAdminRole && p.task.assigned_to !== user?.id) return;
      if (!shouldShowTask(p.task, filterStatus)) return;
      setTasks((prev) => {
        if (prev.find((t) => t.id === p.task.id)) return prev;
        return [p.task, ...prev];
      });
      fetchStats();
    });

    const u2 = on("task.updated", (p) => {
      if (!p?.task) return;
      const isMyTask = p.task.assigned_to === user?.id;
      if ((!isAdminRole && !isMyTask) || !shouldShowTask(p.task, filterStatus)) {
        // Task was reassigned away from this agent — remove from state
        setTasks((prev) => prev.filter((t) => t.id !== p.task.id));
        fetchStats();
      } else {
        setTasks((prev) => prev.map((t) => t.id === p.task.id ? p.task : t));
      }
    });

    const u3 = on("task.deleted", (p) => {
      if (p?.task_id) {
        setTasks((prev) => prev.filter((t) => t.id !== p.task_id));
        fetchStats();
      }
    });

    return () => { u1?.(); u2?.(); u3?.(); };
  }, [on, user, fetchStats, filterStatus]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleComplete = useCallback(async (task) => {
    const newStatus = task.status === "completed" ? "pending" : "completed";
    const optimistic = { ...task, status: newStatus };
    setTasks((prev) => newStatus === "completed" && filterStatus !== "completed"
      ? prev.filter((t) => t.id !== task.id)
      : prev.map((t) => t.id === task.id ? optimistic : t));
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, { status: newStatus });
      setTasks((prev) => shouldShowTask(data, filterStatus)
        ? prev.map((t) => t.id === task.id ? data : t)
        : prev.filter((t) => t.id !== task.id));
      fetchStats();
      toast.success(newStatus === "completed" ? "Tarefa concluída ✓" : "Tarefa reaberta");
    } catch {
      setTasks((prev) => prev.find((t) => t.id === task.id)
        ? prev.map((t) => t.id === task.id ? task : t)
        : [task, ...prev]);
      toast.error("Erro ao atualizar tarefa");
    }
  }, [fetchStats, filterStatus]);

  const handleDelete = useCallback(async (task) => {
    if (!window.confirm(`Excluir "${task.title}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      fetchStats();
      toast.success("Tarefa excluída");
    } catch {
      toast.error("Erro ao excluir tarefa");
    }
  }, [fetchStats]);

  const handleSaved = useCallback((savedTask, isEdit) => {
    if (!shouldShowTask(savedTask, filterStatus)) {
      setTasks((prev) => prev.filter((t) => t.id !== savedTask.id));
      fetchStats();
      return;
    }
    if (isEdit) {
      setTasks((prev) => prev.map((t) => t.id === savedTask.id ? savedTask : t));
    } else {
      setTasks((prev) => {
        if (prev.find((t) => t.id === savedTask.id)) return prev;
        return [savedTask, ...prev];
      });
    }
    fetchStats();
  }, [fetchStats, filterStatus]);

  const handleDeleted = useCallback((taskId) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    fetchStats();
  }, [fetchStats]);

  const openCreate = () => { setDrawerTask(null); setDrawerOpen(true); };
  const openEdit = (task) => { setDrawerTask(task); setDrawerOpen(true); };

  // Leva o lojista do card da Task direto pro chat do lead correspondente,
  // já com a mensagem sugerida (se houver) pronta pra revisar/enviar.
  const handleOpenChat = (task) => {
    if (!task.lead_id) return;
    navigate("/", { state: { openLeadId: task.lead_id, prefillMessage: task.suggested_message || null } });
  };
  const closeDrawer = () => setDrawerOpen(false);

  // ── Drag and Drop ──────────────────────────────────────────────────────────

  const handleDragStart = ({ active }) => {
    const task = active.data.current?.task;
    setActiveTask(task || null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveTask(null);
    if (!over || !active) return;
    const task = active.data.current?.task;
    const targetColId = over.id;
    if (!task || !targetColId) return;

    const currentCol = getTaskCol(task, filterStatus);
    if (currentCol === targetColId) return;

    const col = KANBAN_COLS.find((c) => c.id === targetColId);
    if (col?.noDropTarget) {
      toast.error("Tarefas atrasadas são calculadas automaticamente");
      return;
    }

    const newStatus = COL_STATUS_MAP[targetColId];
    if (!newStatus) return;

    // Optimistic
    setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, status: newStatus } : t));

    try {
      const { data } = await api.patch(`/tasks/${task.id}`, { status: newStatus });
      setTasks((prev) => prev.map((t) => t.id === task.id ? data : t));
      toast.success("Tarefa movida");
      fetchStats();
    } catch {
      setTasks((prev) => prev.map((t) => t.id === task.id ? task : t));
      toast.error("Erro ao mover tarefa");
    }
  };

  // ── Filter quick-stats ─────────────────────────────────────────────────────

  const activateFilter = (key) => {
    setFilterStatus("");
    setFilterPriority("");
    setFilterMine(false);
    setFilterOverdue(false);
    if (key === "overdue") setFilterOverdue(true);
    else if (key === "mine") setFilterMine(true);
    else if (key === "status_pending") setFilterStatus("pending");
  };

  // ── Kanban grouping ────────────────────────────────────────────────────────

  const tasksByCol = useMemo(() => {
    const map = {};
    const cols = filterStatus === "completed" ? [COMPLETED_KANBAN_COL] : KANBAN_COLS;
    cols.forEach((c) => (map[c.id] = []));
    tasks.forEach((t) => {
      const col = getTaskCol(t, filterStatus);
      if (!col) return;
      (map[col] = map[col] || []).push(t);
    });
    return map;
  }, [tasks, filterStatus]);

  const kanbanColumns = useMemo(
    () => (filterStatus === "completed" ? [COMPLETED_KANBAN_COL] : KANBAN_COLS),
    [filterStatus]
  );

  // ── Calendar fetch (different endpoint for month view) ────────────────────

  const handleMonthChange = (dir) => {
    let m = calMonth + dir;
    let y = calYear;
    if (m > 12) { m = 1; y++; }
    if (m < 1)  { m = 12; y--; }
    setCalMonth(m);
    setCalYear(y);
  };

  const calendarTasks = useMemo(() => {
    if (view !== "calendar") return [];
    return tasks.filter((t) => t.due_date);
  }, [tasks, view]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      {/* Webkit scrollbar hide for task kanban */}
      <style>{`.task-kanban-scroll::-webkit-scrollbar { display: none; }`}</style>
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar title="Tarefas" subtitle="Gerencie e acompanhe todas as tarefas da equipe em tempo real" />

        <div className="flex-1 overflow-auto px-6 py-5 flex flex-col gap-5">

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total" value={stats.total} onClick={() => activateFilter("all")} />
            <StatCard label="Pendentes" value={stats.pending} color="text-slate-300"
              onClick={() => activateFilter("status_pending")} active={filterStatus === "pending"} />
            <StatCard label="Atrasadas" value={stats.overdue} color="text-red-400"
              onClick={() => activateFilter("overdue")} active={filterOverdue} />
            <StatCard label="Minhas" value={stats.mine} color="text-[#25D366]"
              onClick={() => activateFilter("mine")} active={filterMine} />
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar tarefas..."
                className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-sm text-slate-100 outline-none focus:border-[#25D366] transition"
              />
            </div>

            {/* Status filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-sm text-slate-300 outline-none focus:border-[#25D366] transition"
            >
              <option value="">Todos os status</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>

            {/* Priority filter */}
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-sm text-slate-300 outline-none focus:border-[#25D366] transition"
            >
              <option value="">Toda prioridade</option>
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>

            {/* Quick toggles */}
            <button
              onClick={() => setFilterMine((v) => !v)}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${filterMine ? "border-[#25D366]/40 bg-[#25D366]/10 text-[#25D366]" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}
            >Minhas</button>
            <button
              onClick={() => setFilterOverdue((v) => !v)}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${filterOverdue ? "border-red-500/40 bg-red-500/10 text-red-400" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}
            >Atrasadas</button>

            {/* Clear filters */}
            {(filterStatus || filterPriority || filterMine || filterOverdue || search) && (
              <button
                onClick={() => { setSearch(""); setFilterStatus(""); setFilterPriority(""); setFilterMine(false); setFilterOverdue(false); }}
                className="p-2 text-slate-500 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition"
                title="Limpar filtros"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            <div className="ml-auto flex items-center gap-2">
              {/* Refresh */}
              <button onClick={() => { fetchTasks(); fetchStats(); }}
                className="p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-lg transition"
                title="Atualizar"
              >
                <RefreshCw className="w-4 h-4" />
              </button>

              {/* View toggle — labels visíveis, estilo padrão CRM */}
              <div className="flex bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                {[
                  { id: "kanban",   icon: LayoutGrid,  label: "Kanban"     },
                  { id: "list",     icon: List,        label: "Lista"       },
                  { id: "calendar", icon: CalendarDays, label: "Calendário" },
                ].map(({ id, icon: Icon, label }) => (
                  <button
                    key={id}
                    onClick={() => setView(id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                      view === id
                        ? "bg-slate-800 text-[#25D366]"
                        : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>

              {/* New task */}
              <button
                onClick={openCreate}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 text-sm font-bold transition"
              >
                <Plus className="w-4 h-4" />
                Nova Tarefa
              </button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-[#25D366] animate-spin" />
            </div>
          ) : (
            <>
              {/* ── Kanban ── */}
              {view === "kanban" && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCorners}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <div
                    ref={kanbanScrollRef}
                    className="flex gap-4 overflow-x-auto pb-4 flex-1 task-kanban-scroll"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none", cursor: "default" }}
                    onMouseDown={handleKanbanMouseDown}
                    onMouseMove={handleKanbanMouseMove}
                    onMouseUp={handleKanbanMouseUp}
                    onMouseLeave={handleKanbanMouseUp}
                  >
                    {kanbanColumns.map((col) => (
                      <KanbanTaskColumn
                        key={col.id}
                        col={col}
                        tasks={tasksByCol[col.id] || []}
                        onEdit={openEdit}
                        onComplete={handleComplete}
                        onDelete={handleDelete}
                        onOpenChat={handleOpenChat}
                      />
                    ))}
                  </div>
                  <DragOverlay dropAnimation={{ duration: 200, easing: "ease" }}>
                    {activeTask ? <GhostCard task={activeTask} /> : null}
                  </DragOverlay>
                </DndContext>
              )}

              {/* ── List ── */}
              {view === "list" && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden flex-1">
                  {tasks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3">
                      <CheckSquare className="w-12 h-12 text-slate-700" />
                      <p className="text-slate-500 text-sm">Nenhuma tarefa encontrada</p>
                      <button onClick={openCreate} className="text-[#25D366] text-sm hover:underline">Criar primeira tarefa</button>
                    </div>
                  ) : (
                    tasks.map((task) => (
                      <TaskListRow key={task.id} task={task} onEdit={openEdit} onComplete={handleComplete} onDelete={handleDelete} onOpenChat={handleOpenChat} />
                    ))
                  )}
                </div>
              )}

              {/* ── Calendar ── */}
              {view === "calendar" && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex-1">
                  <CalendarView
                    tasks={calendarTasks}
                    onEdit={openEdit}
                    year={calYear}
                    month={calMonth}
                    onMonthChange={handleMonthChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Task Drawer */}
      <TaskDrawer
        open={drawerOpen}
        task={drawerTask}
        onClose={closeDrawer}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
        team={team}
        currentUser={user}
      />
    </div>
  );
}
